import type { FC, HTMLAttributes } from 'react';
import { forwardRef, memo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import type { DensityType } from '../../../utils/density';
import { Tooltip } from '../../GraphModal/Tooltip';
import DensityIcon from '../../icons/DensityIcon';

type TopicDensityItemProps = HTMLAttributes<HTMLDivElement> & {
  densityType: DensityType;
  isActive?: boolean;
  showLabel?: boolean;
  withTooltip?: boolean;
};

export const densityMessages = defineMessages({
  compact: { defaultMessage: 'compact', id: 'sob+Lf' },
  standard: { defaultMessage: 'standard', id: 'qxqUH4' },
  card: { defaultMessage: 'card', id: 'mpJrBL' },
});

const BasicDensityItem: FC<TopicDensityItemProps> = forwardRef<
  HTMLDivElement,
  TopicDensityItemProps
>((props, ref) => {
  const { densityType, isActive, showLabel, className, ...otherProps } = props;

  return (
    <div
      ref={ref}
      className={twMerge(
        `density-${densityType} flex items-center gap-x-4`,
        isActive && 'bg-primary/65',
        props.onClick && ' cursor-pointer',
        className,
      )}
      {...otherProps}
    >
      <div className={twMerge('density-icon text-gray-soft', isActive && 'text-white')}>
        <DensityIcon densityType={densityType} />
      </div>
      {showLabel && (
        <div
          className={twMerge(
            'density-label first-letter:uppercase',
            isActive && 'text-white font-semibold',
          )}
        >
          <FormattedMessage {...densityMessages[densityType]} />
        </div>
      )}
    </div>
  );
});

export const DensityItem: FC<TopicDensityItemProps> = memo(function TopicDensityItem(props) {
  const { withTooltip, ...propsToKeep } = props;

  return withTooltip ? (
    <Tooltip
      content={<FormattedMessage {...densityMessages[propsToKeep.densityType]} />}
      className="capitalize"
    >
      <BasicDensityItem {...propsToKeep} />
    </Tooltip>
  ) : (
    <BasicDensityItem {...propsToKeep} />
  );
});
